import React, { useState } from 'react';
import { Button, CircularProgress, FormHelperText, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getUploadData, resetFileUpload } from '../../../store/upload/uploadSlice';
import MetadataContainer from '../MetadataContainer';
import ProcessButton from '../../ProcessButton';
import { FieldState, Processors } from '../../../types/generic';
import FormTextField from '../../Form/FormTextField';
import Logger from '../../../utils/logger';
import { checkRequired, handleFieldValueUpdate } from '../../../utils/form';
import { anprInitialState } from '../../../utils/initialStates';
import { ANPRMessage } from '../../../types/upload';
import { callAPISync, createUploadHeader, logProcessingStarted, publishMessage, sendSubscribeMessage } from '../../../utils/api';
import { getUserDetails, getUserOrgsString } from '../../../store/user/userSlice';
import { SubscriptionOptions } from '../../../utils/processorTopics';
import { beginProcessing } from '../../../store/processing/processingSlice';
import FileTypeSelector from '../../FileTypeSelector';
import { convertFileFormatToText, getANPRTypes } from '../../../utils/string';
import { uploadAzureFile } from '../../../utils/azure';
import { Info } from '@mui/icons-material';
import MultilineTooltip from '../../MultilineTooltip';
import { ANPR_HEADERS } from '../../../utils/sheets';
import { useLogto } from '@logto/react';
import { UploadFormStep } from '../../Tabs/Upload/UploadTabSkeleton/stepWrapper';
import { useUploadFormStepper } from '../../Tabs/Upload/UploadFormContext';

const ANPRMetadata: React.FC<{ index?: number }> = ({ index: baseIndex = 0 }) => {
	const dispatch = useAppDispatch();
	const logto = useLogto();
	const possibleAttribFormats = new Set(getANPRTypes().map(convertFileFormatToText));

	const userDetails = useAppSelector((state) => getUserDetails(state));
	const forceDetails = useAppSelector((state) => state.system.forceDetails);
	const userTeams = useAppSelector((state) => getUserOrgsString(state));
	const notifyOnComplete = useAppSelector((state) => state.processing.notifyOnComplete);

	const {
		fileName,
		fileOperation,
		deviceOwner,
		deviceOwnerType,
		uploadComplete,
		fileId,
		uploadReason,
		duplicatePreference,
		useCase,
		enrichments,
	} = useAppSelector((state) => getUploadData(state));

	const [fieldValues, setFieldValues] = useState<FieldState>(anprInitialState);
	const [attribFile, setAttribFile] = useState<File | null>(null);
	const [uploadingAttribFile, setUploadingAttribFile] = useState<boolean>(false);
	const [attribError, setAttribError] = useState<string>('');
	const [publishError, setPublishError] = useState<string>('');
	const [selectedFormat, setSelectedFormat] = useState<string>('');

	const attribFileIsSheet = (): boolean => {
		return ['XLS', 'XLSX'].includes(selectedFormat);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		handleFieldValueUpdate(name, value, fieldValues, setFieldValues);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];

		if (file) {
			callAPISync(logto, (token) => {
				uploadAzureFile(fileOperation, file, token)
					.then(() => {
						setAttribFile(file);
						setSelectedFormat(convertFileFormatToText(file.type));
						setAttribError('');
					})
					.catch((e) => {
						setAttribError(String(e));
					});
			});
		} else {
			setAttribFile(null);
			setSelectedFormat('');
			setAttribError('');
		}

		setUploadingAttribFile(false);
	};

	const handleUploadClick = () => {
		document.getElementById('metadataFileInput')?.click();
	};

	const createANPRMessage = async (): Promise<ANPRMessage> => {
		const anprMessage: ANPRMessage = {
			...createUploadHeader(deviceOwner, fileName, userDetails, fileOperation, uploadReason, forceDetails, useCase, deviceOwnerType),
			metadata: {},
		};

		if (attribFile) {
			anprMessage.metadata = {
				attributionSheet: attribFile.name,
				identifierColumn: fieldValues.identifierColumn.value.toString(),
				attributionColumn: fieldValues.attributedColumn.value.toString(),
			};

			if (attribFileIsSheet()) {
				anprMessage.metadata.sheetName = fieldValues.sheetName.value.toString();
			}
		}

		return anprMessage;
	};

	const onSubmit = () => {
		const tempVals = { ...fieldValues };
		const fileErr = '';

		checkRequired(tempVals);

		if (attribFile) {
			if (attribFileIsSheet() && !fieldValues.sheetName.value) {
				tempVals.sheetName.errorText.add('A Sheet Name must be provided if an attribution spreadsheet is uploaded.');
				tempVals.sheetName.error = true;
			}
			if (!fieldValues.identifierColumn.value) {
				tempVals.identifierColumn.errorText.add('An Identifier column must be provided if an attribution file is uploaded.');
				tempVals.identifierColumn.error = true;
			}
			if (!fieldValues.attributedColumn.value) {
				tempVals.attributedColumn.errorText.add('A Name column must be provided if an attribution file is uploaded.');
				tempVals.attributedColumn.error = true;
			}
		}

		if (Object.values(fieldValues).filter((val) => val.error === true).length === 0 && !fileErr) {
			createANPRMessage().then((anprMessage) => {
				callAPISync(logto, (token) => {
					logProcessingStarted(
						token,
						fileOperation,
						fileName,
						uploadReason,
						fileId,
						userDetails.name,
						userTeams,
						userDetails.email,
						undefined, // undefined as no device owner in anpr
						undefined,
						undefined,
						notifyOnComplete
					);
					sendSubscribeMessage(SubscriptionOptions.ANPR, token);
					publishMessage(anprMessage, 'upload.anpr', fileId, token, duplicatePreference)
						.then(async (res) => {
							const txt = await res.text();
							if (res.ok) {
								Logger.debug('Submitted:\n', txt);
								dispatch(
									beginProcessing({
										fileName,
										fileId,
										operation: fileOperation,
										processor: Processors.ANPR,
										duplicatePreference,
										enrichments,
									})
								);
								dispatch(resetFileUpload());
							} else {
								Logger.error('Error submitting');
								Logger.error(res.status, res.statusText);
								setPublishError(res.statusText);
							}
						})
						.catch((e) => {
							Logger.error(e);
							setPublishError(JSON.stringify(e));
						});
				});
			});
		} else {
			Logger.debug('Errors found');
		}

		setFieldValues({
			...tempVals,
		});
		setAttribError(fileErr);
	};

	const getExtraMetadata = () => {
		if (attribFileIsSheet()) {
			return (
				<FormTextField
					fieldData={fieldValues.sheetName}
					textFieldProps={{
						label: 'Excel Sheet Name',
						name: 'sheetName',
						onChange: handleInputChange,
						disabled: !uploadComplete,
					}}
				/>
			);
		} else {
			return <></>;
		}
	};

	const steps = [
		<UploadFormStep key="anprMetadata" label="ANPR Metadata" valid={true} validLabel="ANPR Metadata">
			<Stack direction={'row'} display={'flex'} alignItems={'center'} width="100%" justifyContent={'center'}>
				<Typography sx={{ width: '80%' }}>
					Currently only ANPR files with specific groups of headers are supported. For more information hover over this icon:
				</Typography>
				<MultilineTooltip
					title={[
						'Supported heading groups (headings can be in any order):',
						...ANPR_HEADERS.map((header: string[]) => `- ${header.join(', ')}`),
					]}
				>
					<Info fontSize="large" />
				</MultilineTooltip>

				<MetadataContainer title="Metadata">
					<Stack direction={'row'} display={'flex'} justifyContent={'space-between'} alignItems={'center'} marginBottom={3} marginTop={0}>
						<Typography variant="body1" color={attribError && 'error'}>
							Attribution Spreadsheet/CSV: {attribFile ? attribFile.name : 'None'}
						</Typography>
						<Button variant="contained" disabled={!uploadComplete} onClick={handleUploadClick}>
							{uploadingAttribFile ? <CircularProgress size="2em" aria-label="Upload Attribution File Progress Spinner" /> : 'Upload'}
						</Button>
						<input
							id="metadataFileInput"
							type="file"
							hidden
							onChange={(e) => {
								setUploadingAttribFile(true);
								handleFileChange(e);
							}}
							data-testid="metadata-file-upload-input"
						/>
					</Stack>
					<FormHelperText error={Boolean(attribError)} sx={{ paddingBottom: 1 }} data-testid="file-helper-text">
						{attribError ? `${attribError} ` : ''}Attribution sheet mapping VRN to User. If not provided will be automatically generated as
						&quot;Driver of [VRN]&quot;
					</FormHelperText>

					<FileTypeSelector
						disabled={!attribFile}
						selected={selectedFormat}
						setSelected={setSelectedFormat}
						options={Array.from(possibleAttribFormats)}
						detectedFormat={convertFileFormatToText(attribFile ? attribFile.type : '')}
					/>

					{getExtraMetadata()}

					<FormTextField
						fieldData={fieldValues.identifierColumn}
						textFieldProps={{
							label: 'Spreadsheet column containing identifiers to be attributed',
							name: 'identifierColumn',
							onChange: handleInputChange,
							disabled: !uploadComplete,
						}}
					/>

					<FormTextField
						fieldData={fieldValues.attributedColumn}
						textFieldProps={{
							label: 'Spreadsheet column containing the attributed names',
							name: 'attributedColumn',
							onChange: handleInputChange,
							disabled: !uploadComplete,
						}}
					/>
				</MetadataContainer>
			</Stack>
		</UploadFormStep>,
		<UploadFormStep key="cellebriteProcess" label="Final Confirmation" valid={true} validLabel="">
			<ProcessButton
				operation={fileOperation}
				onClick={onSubmit}
				pipelineName="Cellebrite"
				publishError={publishError}
				setPublishError={setPublishError}
			/>
		</UploadFormStep>,
	];

	const { activeStep } = useUploadFormStepper();

	return (
		<>
			{steps.map((step, i) => {
				const stepIndex = baseIndex + i;

				return React.cloneElement(step, {
					index: stepIndex,
					active: activeStep === stepIndex,
					completed: activeStep > stepIndex,
				});
			})}
		</>
	);
};

export default ANPRMetadata;
